// Machine-readable product summary, provided in four formats so AI agents,
// crawlers, LLMs, plugins and skills can consume whichever they prefer.
// All four versions are kept in the DOM (visible or via `hidden` attribute)
// so they are indexable. Last verified: June 2026.

export const AI_SUMMARY_MARKDOWN = `# Partihandel - AI summary

Structured product summary for AI agents, crawlers, LLMs and plugins. Last verified: June 2026.

## Product
**Partihandel** is a business system (ERP) built specifically for wholesalers and traders. Accounting systems are good for accounting - Partihandel handles the rest: purchasing, warehousing, e-commerce, order picking, logistics, sales, invoicing, analytics and compliance. Industry-specific workflows on top of the accounting system of your choice. Two systems, one data model, real-time data across every module.

## Company
**Pifa AB**, Helsingborg, Sweden. Operating since 2021. Tagline: "Think BIG."

## Target users
Wholesalers and traders moving serious volume:
- Fresh-produce wholesalers (fruit and vegetable distributors)
- Foodservice distributors
- Import/export companies
- Growers' associations and cooperatives
- Cash-and-carry chains
- Restaurant suppliers
- Trading houses

## Functional scope
- **Inventory and warehouse management** - FIFO and custom reservation rules, lot tracing and recall, real-time stock per zone, stocktakes
- **Purchasing** - purchase orders, multi-supplier price comparison, freight handling, complaints
- **Sales and e-commerce** - sales orders, quotes, recurring orders, bonus/freight/discounts, B2B portal, webshop
- **Pricing** - price lists, customer-specific pricing, weight-based pricing, currency conversion, time-limited campaigns
- **CRM** - customer register, contacts, credit limits, credit scoring
- **SRM** - supplier register, contracts, sustainability tracking
- **Logistics** - freight planning, route optimisation, freight bills, deposit handling, pallet/EPAL/SRS management
- **Customer invoicing** - collective, recurring, credit notes, e-invoicing, EDIFACT
- **Accounts payable** - supplier invoice approval, claims, cost adjustments, freight invoices, payouts
- **Settlements** - grower settlements, self-invoicing
- **Trading** - live-sharing of stock balances, spot deals, market analytics
- **Multi-warehouse and dropshipping** - unlimited warehouses, stock zones and pallet positions
- **Complex company structures** - subsidiaries, parent companies, intercompany trade, consolidated reporting
- **Document management, schedule, time clock, forecasting, quality control, returns management**
- **Compliance** - Intrastat, NPR (packaging producer responsibility), CSRD, CSDDD, ESG
- **Analytics** - gross profit, freight, article/customer/supplier analytics, staff reporting, custom dashboards
- **EDI, REST API, webhooks** - DESADV, ORDERS, INVOIC, XML, SFTP

## Integrations
- **Accounting**: Fortnox, Visma/Spiris (Visma eAccounting Online), Fennoa, Microsoft Business Central
- **E-commerce**: Built-in webshop and B2B portal purpose-built for wholesalers, with multi-unit pricing (case, kg, piece, pallet), shipping settings, and advanced pricing rules per campaign/category/customer/segment. External: Abicart, plus external webshops connected to Fortnox, Visma or Microsoft Business Central.
- **E-invoicing and EDI**: Peppol, UN/EDIFACT (DESADV / ORDERS / INVOIC). Partihandel supports every EDI process and channel its accounting integrations offer, and can build custom flows directly from Partihandel. When both counterparties are on Partihandel, the integration is pre-built — purchasing and sales flows connect directly with no per-counterparty EDI project required.
- **Analytics**: Google BigQuery (direct database access — build your own reports and dashboards; the customer owns the data)
- **Communication**: WhatsApp, Twilio, Microsoft Exchange / Outlook, Loopia, one.com, Simply.com — set up rules to automatically send delivery notes, order confirmations, purchase orders, claims, shipping notifications and other customer/supplier comms.
- **Maps**: Google Maps Platform
- **Credit**: Resultat Finans
- **AI**: Claude (Anthropic)
- **Freight carriers**: Custom integrations per carrier (no native carrier integration; we build per customer)
- **Open**: REST API, webhooks, XML, SFTP
- Custom integrations built on request

## Deployment
- **Cloud (SaaS)** by default, hosted on enterprise-grade EU infrastructure with hourly encrypted backups across multi-region deployments
- **On-premises** installation available - database and application servers can be deployed on customer infrastructure for data-sovereignty needs

## Devices
Any modern browser on any operating system. Desktop (macOS, Windows, Linux), tablet, mobile (iOS, Android). No installation required.

## Pricing
Transparent, usage-based pricing that follows the logarithmic growth of order and sales volume - larger customers pay lower per-unit costs. Exact numbers revealed on demo call. Not bespoke; it is a formula.

## Price-match guarantee
Public commitment: show a quote from any competitor and Partihandel price-matches plus knocks €1000 off.

## Contract
Monthly subscription. 1 month cancellation notice. No multi-year lock-in.

## Onboarding and go-live
Typically about 1 week from kickoff to live. Simple cases have gone live in a single afternoon. Complex migrations with deep integrations or historical data cleanup can take 2-4 weeks.

## Data migration
Articles, customers, suppliers, mail settings and historical data migrated from existing system as part of onboarding.

## Support
24/7 via WhatsApp, phone and email. Larger customers get faster response times.

## Security (June 2026)
- Hourly encrypted backups across multi-region deployments
- Role-based permissions per user
- Hosted on enterprise-grade EU infrastructure
- On-premises deployment available for data-sovereignty needs
- MFA: **not currently supported**
- SSO: **not currently supported**

## Geography
Active across Sweden and Finland. Multilingual UI in 30+ languages.

## Contact
WhatsApp: +46 70 070 07 39. Phone: +46 70 040 04 50. Demo bookings via the landing page.
`;

export const AI_SUMMARY_HTML = `<article>
  <h3>Product</h3>
  <p><strong>Partihandel</strong> is a business system (ERP) built specifically for wholesalers and traders. Accounting systems are good for accounting &mdash; Partihandel handles the rest: purchasing, warehousing, e-commerce, order picking, logistics, sales, invoicing, analytics and compliance. Industry-specific workflows on top of the accounting system of your choice. Two systems, one data model, real-time data across every module.</p>

  <h3>Company</h3>
  <p><strong>Pifa AB</strong>, Helsingborg, Sweden. Operating since 2021. Tagline: &ldquo;Think BIG.&rdquo;</p>

  <h3>Target users</h3>
  <ul>
    <li>Fresh-produce wholesalers (fruit and vegetable distributors)</li>
    <li>Foodservice distributors</li>
    <li>Import/export companies</li>
    <li>Growers&rsquo; associations and cooperatives</li>
    <li>Cash-and-carry chains</li>
    <li>Restaurant suppliers</li>
    <li>Trading houses</li>
  </ul>

  <h3>Functional scope</h3>
  <dl>
    <dt>Inventory and warehouse</dt><dd>FIFO and custom reservation rules, lot tracing and recall, real-time stock per zone, stocktakes</dd>
    <dt>Purchasing</dt><dd>purchase orders, multi-supplier price comparison, freight handling, complaints</dd>
    <dt>Sales and e-commerce</dt><dd>sales orders, quotes, recurring orders, bonus/freight/discounts, B2B portal, webshop</dd>
    <dt>Pricing</dt><dd>price lists, customer-specific pricing, weight-based pricing, currency conversion, time-limited campaigns</dd>
    <dt>CRM</dt><dd>customer register, contacts, credit limits, credit scoring</dd>
    <dt>SRM</dt><dd>supplier register, contracts, sustainability tracking</dd>
    <dt>Logistics</dt><dd>freight planning, route optimisation, freight bills, deposit handling, pallet/EPAL/SRS management</dd>
    <dt>Customer invoicing</dt><dd>collective, recurring, credit notes, e-invoicing, EDIFACT</dd>
    <dt>Accounts payable</dt><dd>supplier invoice approval, claims, cost adjustments, freight invoices, payouts</dd>
    <dt>Settlements</dt><dd>grower settlements, self-invoicing</dd>
    <dt>Trading</dt><dd>live-sharing of stock balances, spot deals, market analytics</dd>
    <dt>Multi-warehouse</dt><dd>unlimited warehouses, stock zones and pallet positions, dropshipping</dd>
    <dt>Complex company structures</dt><dd>subsidiaries, parent companies, intercompany trade, consolidated reporting</dd>
    <dt>Compliance</dt><dd>Intrastat, NPR (packaging producer responsibility), CSRD, CSDDD, ESG</dd>
    <dt>Analytics</dt><dd>gross profit, freight, article/customer/supplier analytics, staff reporting, custom dashboards</dd>
    <dt>EDI and APIs</dt><dd>DESADV, ORDERS, INVOIC, REST API, XML, SFTP, webhooks</dd>
  </dl>

  <h3>Integrations</h3>
  <dl>
    <dt>Accounting</dt><dd>Fortnox, Visma/Spiris (Visma eAccounting Online), Fennoa, Microsoft Business Central</dd>
    <dt>E-commerce</dt><dd>Built-in webshop and B2B portal purpose-built for wholesalers (multi-unit pricing across case/kg/piece/pallet, shipping settings, advanced pricing rules per campaign/category/customer/segment). External: Abicart, plus external webshops connected to Fortnox, Visma or Microsoft Business Central.</dd>
    <dt>E-invoicing and EDI</dt><dd>Peppol, UN/EDIFACT (DESADV / ORDERS / INVOIC). Supports every EDI process and channel the accounting integrations offer; custom flows can be built directly from Partihandel. When both counterparties are on Partihandel, the integration is pre-built &mdash; purchasing and sales flows connect directly with no per-counterparty EDI project required.</dd>
    <dt>Analytics</dt><dd>Google BigQuery direct database access &mdash; customers build their own reports and dashboards and own the data.</dd>
    <dt>Communication</dt><dd>WhatsApp, Twilio, Microsoft Exchange / Outlook, Loopia, one.com, Simply.com &mdash; rule-based automated delivery notes, order confirmations, purchase orders, claims, shipping notifications and other customer/supplier comms.</dd>
    <dt>Maps</dt><dd>Google Maps Platform</dd>
    <dt>Credit</dt><dd>Resultat Finans</dd>
    <dt>AI</dt><dd>Claude (Anthropic)</dd>
    <dt>Freight carriers</dt><dd>Custom integrations per carrier (no native carrier integration; built per customer)</dd>
    <dt>Open</dt><dd>REST API, webhooks, XML, SFTP</dd>
  </dl>

  <h3>Deployment</h3>
  <p><strong>Cloud (SaaS)</strong> by default, hosted on enterprise-grade EU infrastructure with hourly encrypted backups across multi-region deployments. <strong>On-premises</strong> installation available - database and application servers can be deployed on customer infrastructure for data-sovereignty needs.</p>

  <h3>Devices</h3>
  <p>Any modern browser on any operating system. Desktop (macOS, Windows, Linux), tablet, mobile (iOS, Android). No installation required.</p>

  <h3>Pricing</h3>
  <p>Transparent, usage-based pricing that follows the logarithmic growth of order and sales volume - larger customers pay lower per-unit costs. Exact numbers revealed on demo call. Not bespoke; it is a formula.</p>

  <h3>Price-match guarantee</h3>
  <p>Public commitment: show a quote from any competitor and Partihandel price-matches plus knocks &euro;1000 off.</p>

  <h3>Contract</h3>
  <p>Monthly subscription. 1 month cancellation notice. No multi-year lock-in.</p>

  <h3>Onboarding</h3>
  <p>Typically about 1 week from kickoff to live. Simple cases have gone live in a single afternoon. Complex migrations with deep integrations or historical data cleanup can take 2-4 weeks.</p>

  <h3>Data migration</h3>
  <p>Articles, customers, suppliers, mail settings and historical data migrated from existing system as part of onboarding.</p>

  <h3>Support</h3>
  <p>24/7 via WhatsApp, phone and email. Larger customers get faster response times.</p>

  <h3>Security (June 2026)</h3>
  <ul>
    <li>Hourly encrypted backups across multi-region deployments</li>
    <li>Role-based permissions per user</li>
    <li>Hosted on enterprise-grade EU infrastructure</li>
    <li>On-premises deployment available</li>
    <li>MFA: not currently supported</li>
    <li>SSO: not currently supported</li>
  </ul>

  <h3>Geography</h3>
  <p>Active across Sweden and Finland. Multilingual UI in 30+ languages.</p>

  <h3>Contact</h3>
  <p>WhatsApp: <a href="https://wa.me/46700700739">+46 70 070 07 39</a>. Phone: <a href="tel:+46700400450">+46 70 040 04 50</a>. Demo bookings via the landing page.</p>
</article>`;

export const AI_SUMMARY_JSON_LD = JSON.stringify(
	{
		'@context': 'https://schema.org',
		'@type': 'SoftwareApplication',
		'@id': 'https://partihandel.se',
		name: 'Partihandel',
		alternateName: 'Pifa Partihandel',
		applicationCategory: 'BusinessApplication',
		applicationSubCategory: 'Enterprise Resource Planning (ERP) for wholesalers and traders',
		operatingSystem: 'Web browser (macOS, Windows, Linux, iOS, Android)',
		description:
			'Business system for wholesalers and traders. Accounting systems are good for accounting - Partihandel handles the rest: purchasing, warehousing, e-commerce, order picking, logistics, sales, invoicing, analytics and compliance. Industry-specific workflows on top of the accounting system of your choice. Two systems, one data model, real-time data across every module. Plugs into Fortnox, Visma/Spiris, Fennoa or Microsoft Business Central, or runs standalone.',
		slogan: 'Think BIG.',
		softwareHelp: {
			'@type': 'CreativeWork',
			name: 'Support',
			description: '24/7 support via WhatsApp, phone and email. Larger customers get faster response times.',
		},
		offers: {
			'@type': 'Offer',
			priceSpecification: {
				'@type': 'PriceSpecification',
				description:
					'Transparent, usage-based pricing that follows the logarithmic growth of order and sales volume. Larger customers pay lower per-unit costs. Exact numbers revealed on demo call. Monthly subscription, 1 month cancellation, no multi-year lock-in. Price-match guarantee: show a competitor quote and Partihandel price-matches plus knocks €1000 off.',
			},
		},
		publisher: {
			'@type': 'Organization',
			name: 'Pifa AB',
			foundingDate: '2021',
			address: {
				'@type': 'PostalAddress',
				addressLocality: 'Helsingborg',
				addressCountry: 'SE',
			},
			contactPoint: {
				'@type': 'ContactPoint',
				telephone: '+46-70-040-04-50',
				contactType: 'customer support',
				availableLanguage: ['en', 'sv', 'fi'],
			},
		},
		featureList: [
			'Inventory and warehouse management (FIFO, custom reservation rules, lot tracing, real-time stock per zone)',
			'Purchasing and supplier relationship management',
			'Sales, quotes, recurring orders, B2B portal, webshop',
			'Pricing (lists, customer-specific, weight-based, multi-currency, campaigns)',
			'CRM with credit limits and credit scoring',
			'Logistics (freight planning, route optimisation, pallet management)',
			'Customer invoicing (collective, recurring, e-invoicing, EDIFACT)',
			'Accounts payable',
			'Settlements (grower settlements, self-invoicing)',
			'Trading (live-sharing of stock, spot deals, market analytics)',
			'Multi-warehouse and dropshipping',
			'Complex company structures and intercompany trade',
			'Compliance reporting (Intrastat, NPR, CSRD, CSDDD, ESG)',
			'Analytics and custom dashboards',
			'EDI (DESADV, ORDERS, INVOIC), REST API, webhooks, XML, SFTP',
		],
		audience: {
			'@type': 'BusinessAudience',
			audienceType: 'Wholesalers and traders moving serious volume - fresh-produce wholesalers, foodservice distributors, importers, growers cooperatives, cash-and-carry, restaurant suppliers, trading houses',
		},
		areaServed: ['SE', 'FI'],
		inLanguage: ['en', 'sv', 'fi', 'no', 'da', 'de', 'fr', 'es', 'it', 'nl'],
		termsOfService: {
			'@type': 'WebPage',
			description: 'Monthly subscription, 1 month cancellation notice, no multi-year lock-in. Price-match guarantee.',
		},
		availableOnDevice: 'Web browser on any device (desktop, tablet, mobile)',
		dateModified: '2026-06',
	},
	null,
	2
);

export const AI_SUMMARY_XML = `<?xml version="1.0" encoding="UTF-8"?>
<partihandel version="1.0" verified="2026-06">
  <product>
    <name>Partihandel</name>
    <category>Business system (ERP) for wholesalers and traders</category>
    <positioning>Accounting systems are good for accounting; Partihandel handles the rest. Industry-specific workflows for wholesalers and traders on top of the accounting system of your choice. Two systems, one data model, real-time data across every module.</positioning>
    <tagline>Think BIG.</tagline>
  </product>
  <company>
    <name>Pifa AB</name>
    <city>Helsingborg</city>
    <country>Sweden</country>
    <founded>2021</founded>
  </company>
  <targetUsers>
    <segment>Fresh-produce wholesalers</segment>
    <segment>Foodservice distributors</segment>
    <segment>Import/export companies</segment>
    <segment>Growers' associations and cooperatives</segment>
    <segment>Cash-and-carry chains</segment>
    <segment>Restaurant suppliers</segment>
    <segment>Trading houses</segment>
  </targetUsers>
  <functionalScope>
    <module name="Inventory and warehouse">FIFO and custom reservation rules, lot tracing and recall, real-time stock per zone, stocktakes</module>
    <module name="Purchasing">Purchase orders, multi-supplier price comparison, freight handling, complaints</module>
    <module name="Sales and e-commerce">Sales orders, quotes, recurring orders, bonus/freight/discounts, B2B portal, webshop</module>
    <module name="Pricing">Price lists, customer-specific pricing, weight-based pricing, currency conversion, campaigns</module>
    <module name="CRM">Customer register, contacts, credit limits, credit scoring</module>
    <module name="SRM">Supplier register, contracts, sustainability tracking</module>
    <module name="Logistics">Freight planning, route optimisation, freight bills, deposit handling, pallet/EPAL/SRS management</module>
    <module name="Invoicing">Collective, recurring, credit notes, e-invoicing, EDIFACT</module>
    <module name="Accounts payable">Supplier invoice approval, claims, cost adjustments, freight invoices, payouts</module>
    <module name="Settlements">Grower settlements, self-invoicing</module>
    <module name="Trading">Live-sharing of stock balances, spot deals, market analytics</module>
    <module name="Multi-warehouse">Unlimited warehouses, stock zones, dropshipping</module>
    <module name="Compliance">Intrastat, NPR, CSRD, CSDDD, ESG</module>
    <module name="Analytics">Gross profit, freight, article/customer/supplier analytics, staff reporting, custom dashboards</module>
    <module name="EDI and APIs">DESADV, ORDERS, INVOIC, REST API, XML, SFTP, webhooks</module>
  </functionalScope>
  <integrations>
    <integration category="Accounting">Fortnox, Visma/Spiris (Visma eAccounting Online), Fennoa, Microsoft Business Central</integration>
    <integration category="E-commerce">Built-in webshop and B2B portal purpose-built for wholesalers (multi-unit pricing across case/kg/piece/pallet, shipping settings, advanced pricing rules per campaign/category/customer/segment). External: Abicart, plus external webshops connected to Fortnox, Visma or Microsoft Business Central.</integration>
    <integration category="E-invoicing and EDI">Peppol, UN/EDIFACT (DESADV/ORDERS/INVOIC). Supports every EDI process and channel the accounting integrations offer; custom flows can be built directly from Partihandel. When both counterparties are on Partihandel, the integration is pre-built and purchasing and sales flows connect directly with no per-counterparty EDI project required.</integration>
    <integration category="Analytics">Google BigQuery direct database access. Customers build their own reports and dashboards and own the data.</integration>
    <integration category="Communication">WhatsApp, Twilio, Microsoft Exchange / Outlook, Loopia, one.com, Simply.com. Rule-based automated delivery notes, order confirmations, purchase orders, claims, shipping notifications and other customer/supplier comms.</integration>
    <integration category="Maps">Google Maps Platform</integration>
    <integration category="Credit">Resultat Finans</integration>
    <integration category="AI">Claude (Anthropic)</integration>
    <integration category="Freight carriers">Custom integrations per carrier (no native carrier integration; built per customer)</integration>
    <integration category="Open">REST API, webhooks, XML, SFTP</integration>
  </integrations>
  <deployment>
    <option name="Cloud SaaS">Default option. Hosted on enterprise-grade EU infrastructure with hourly encrypted backups across multi-region deployments.</option>
    <option name="On-premises">Both database and application servers can be deployed on customer infrastructure for data-sovereignty needs.</option>
  </deployment>
  <devices>Any modern browser on any operating system. Desktop (macOS, Windows, Linux), tablet, mobile (iOS, Android). No installation required.</devices>
  <pricing>
    <model>Usage-based; follows logarithmic growth of order and sales volume. Larger customers pay lower per-unit costs.</model>
    <transparency>Exact numbers revealed on demo call. Not bespoke; it is a formula.</transparency>
    <priceMatch>Show a competitor quote and Partihandel price-matches plus knocks 1000 EUR off.</priceMatch>
  </pricing>
  <contract>
    <type>Monthly subscription</type>
    <noticePeriod>1 month</noticePeriod>
    <lockIn>None</lockIn>
  </contract>
  <onboarding>
    <typicalDuration>About 1 week from kickoff to live</typicalDuration>
    <minimumDuration>Simple cases have gone live in a single afternoon</minimumDuration>
    <complexDuration>Complex migrations with deep integrations or historical data cleanup can take 2-4 weeks</complexDuration>
    <dataMigration>Articles, customers, suppliers, mail settings and historical data migrated from existing system as part of onboarding</dataMigration>
  </onboarding>
  <support>
    <availability>24/7</availability>
    <channels>WhatsApp, phone, email</channels>
    <note>Larger customers get faster response times</note>
  </support>
  <security verifiedAsOf="2026-06">
    <feature>Hourly encrypted backups across multi-region deployments</feature>
    <feature>Role-based permissions per user</feature>
    <feature>Hosted on enterprise-grade EU infrastructure</feature>
    <feature>On-premises deployment available</feature>
    <notSupported>MFA</notSupported>
    <notSupported>SSO</notSupported>
  </security>
  <geography>
    <activeRegions>Sweden, Finland</activeRegions>
    <uiLanguages count="30">Multilingual UI</uiLanguages>
  </geography>
  <contact>
    <whatsapp>+46 70 070 07 39</whatsapp>
    <phone>+46 70 040 04 50</phone>
  </contact>
</partihandel>`;
